import { useState, type ChangeEvent, type FormEvent } from "react";
import { Link } from "react-router-dom";
import {
  formSchema, formTemplateVersion, SIGNATURE_CONSENT,
} from "../../../shared/onboardingForms";
import { PAYMENT_TERMS } from "../../../shared/paymentInformation";
import {
  PAYMENT_PREVIEW_ACH_AUTHORIZATION, PAYMENT_PREVIEW_ACH_TERMINATION,
  PAYMENT_PREVIEW_FEE_NOTE, PAYMENT_PREVIEW_TERMS,
} from "../../../shared/paymentInformationPreview";
import "./payment-information-preview.css";

type Method = "credit-card" | "ach";
type FormConfig = { available: boolean; unavailableReason: string | null; adminTesting?: boolean };
type Values = Record<string, string>;
type Attempt = { key: string; snapshot: string };
type FieldErrors = Record<string, string>;
const TYPE = "payment-information" as const;
const VERSION = formTemplateVersion(TYPE);
const EMPTY: Values = {
  method: "credit-card", legalCompanyName: "", cardholder: "", cardNumber: "", expiration: "", cvv: "",
  feeAcknowledgment: "", accountType: "", accountHolder: "", accountNumber: "", routingNumber: "",
  bankName: "", bankAddress: "", bankCityStateZip: "", terms: "", printedName: "", title: "",
};

function makeKey() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, c => {
    const r = Math.random() * 16 | 0;
    return (c === "x" ? r : (r & 3 | 8)).toString(16);
  });
}

function Field({ name, label, value, error, onChange, wide, type = "text", maxLength, inputMode }: {
  name: string; label: string; value: string; error?: string; onChange: (value: string) => void;
  wide?: boolean; type?: string; maxLength?: number; inputMode?: "text" | "numeric";
}) {
  const id = `payment-${name}`;
  return <div className={`payment-preview-field${wide ? " is-wide" : ""}`}>
    <label className="payment-preview-label" htmlFor={id}>{label}</label>
    <input id={id} name={name} type={type} value={value} maxLength={maxLength}
      inputMode={inputMode} autoComplete="off" data-private="true"
      className={`forms-control payment-preview-input${error ? " is-invalid" : ""}`}
      aria-invalid={!!error} aria-describedby={error ? `${id}-error` : undefined}
      onChange={(event: ChangeEvent<HTMLInputElement>) => onChange(event.target.value)} />
    {error && <p className="forms-error" id={`${id}-error`}>{error}</p>}
  </div>;
}

export default function PaymentInformationForm({ config, configError, retryConfig }: {
  config: FormConfig | null; configError: string; retryConfig: () => void;
}) {
  const [method, setMethod] = useState<Method>("credit-card");
  const [values, setValues] = useState<Values>({ ...EMPTY });
  const [signerName, setSignerName] = useState("");
  const [consent, setConsent] = useState(false);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [message, setMessage] = useState("");
  const [reference, setReference] = useState("");
  const [status, setStatus] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [attempt, setAttempt] = useState<Attempt | null>(null);
  const [immutableConflict, setImmutableConflict] = useState(false);
  const [receipt, setReceipt] = useState("");
  const isAvailable = !!config?.available && !configError;

  const cleanData = (source = values) => {
    const data = { ...EMPTY, ...source, method, feeAcknowledgment: method === "credit-card" && source.feeAcknowledgment ? "Yes" : "" };
    if (method === "credit-card") {
      Object.assign(data, { accountType: "", accountHolder: "", accountNumber: "", routingNumber: "", bankName: "", bankAddress: "", bankCityStateZip: "" });
    } else {
      Object.assign(data, { cardholder: "", cardNumber: "", expiration: "", cvv: "", feeAcknowledgment: "" });
    }
    return data;
  };

  const snapshotFor = (data: Values) => JSON.stringify({
    data, signerName: signerName.trim(), consent,
  });

  const update = (name: string, value: string) => {
    setValues(previous => ({ ...previous, [name]: value }));
    setErrors(previous => { const next = { ...previous }; delete next[`data.${name}`]; delete next[name]; return next; });
    setMessage("");
  };

  const selectMethod = (next: Method) => {
    if (next === method || submitting) return;
    setMethod(next);
    setValues(previous => next === "ach"
      ? { ...previous, method: next, cardholder: "", cardNumber: "", expiration: "", cvv: "", feeAcknowledgment: "" }
      : { ...previous, method: next, accountType: "", accountHolder: "", accountNumber: "", routingNumber: "", bankName: "", bankAddress: "", bankCityStateZip: "" });
    setErrors({});
    setMessage("");
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (submitting || !isAvailable) return;
    setMessage("");
    const data = cleanData();
    const parsed = formSchema(TYPE).safeParse({ data, signerName: signerName.trim(), consent, templateVersion: VERSION });
    const nextErrors: FieldErrors = {};
    if (!parsed.success) {
      parsed.error.issues.forEach(issue => {
        const key = issue.path.join(".");
        if (!nextErrors[key]) nextErrors[key] = issue.message;
      });
      setErrors(nextErrors);
      setMessage("Review the highlighted fields and acknowledgments.");
      document.querySelector(".forms-error")?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }

    const snapshot = snapshotFor(parsed.data.data);
    if (attempt && snapshot !== attempt.snapshot) {
      setImmutableConflict(true);
      setMessage("This submission has already been attempted with different answers. Start a new submission to send the updated information.");
      return;
    }
    const currentAttempt = attempt || { key: makeKey(), snapshot };
    if (!attempt) setAttempt(currentAttempt);
    setImmutableConflict(false);
    setErrors({});
    setSubmitting(true);
    try {
      const body = new FormData();
      body.append("payload", JSON.stringify({
        data: parsed.data.data, signerName: parsed.data.signerName,
        consent: parsed.data.consent, templateVersion: VERSION,
      }));
      body.append("idempotencyKey", currentAttempt.key);
      const response = await fetch(`/api/forms/${TYPE}/submissions`, { method: "POST", body });
      const result = await response.json().catch(() => ({})) as { reference?: string; status?: string; error?: string; fieldErrors?: FieldErrors };
      if ((response.status === 200 || response.status === 201) && result.status === "ready" && result.reference) {
        setReceipt(result.reference);
        setValues({ ...EMPTY });
        setMethod("credit-card");
        setSignerName("");
        setConsent(false);
        setAttempt(null);
        setReference("");
        setStatus("ready");
        setMessage("");
        return;
      }
      if (response.status === 400 && result.fieldErrors) {
        setErrors(result.fieldErrors);
        setMessage(result.error || "Some information needs attention.");
        setAttempt(null);
        return;
      }
      if (response.status === 409) {
        setImmutableConflict(true);
        setMessage(result.error || "The server has a different submission recorded for this key. Start a new submission; the existing submission was not changed.");
        return;
      }
      if (result.reference) setReference(result.reference);
      if (result.status) setStatus(result.status);
      if (result.status === "failed" || result.status === "processing" || result.status === "received") {
        setMessage(result.error || (result.status === "failed"
          ? "The submission was not completed. Your answers and retry key are preserved."
          : "The submission is still being processed. Your answers and retry key are preserved."));
        return;
      }
      setMessage(result.error || "We could not confirm receipt. Your answers and the same retry key have been kept.");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "We could not confirm receipt. Your answers and the same retry key have been kept.");
      setStatus("processing");
    } finally {
      setSubmitting(false);
    }
  };

  const startNewAttempt = () => {
    setAttempt(null); setImmutableConflict(false); setMessage(""); setReference(""); setStatus("");
  };
  const field = (name: string, label: string, options: Partial<Parameters<typeof Field>[0]> = {}) =>
    <Field key={name} name={name} label={label} value={values[name] || ""} error={errors[`data.${name}`] || errors[name]} onChange={value => update(name, value)} {...options} />;

  if (receipt) return <main className="forms-shell payment-preview">
    <div className="forms-topline"><span>Premier Pharmaceuticals</span><span>Practice onboarding</span></div>
    <section className="payment-preview-success forms-confirmation" role="status" aria-live="polite">
      <p className="forms-eyebrow">Submission received</p>
      <h1>Thank you. We have it.</h1>
      <p>Your signed payment instructions were received for manual processing. No charge was initiated.</p>
      <div className="forms-reference"><span>Reference</span><strong>{receipt}</strong></div>
      <p className="forms-smallprint">This confirms submission only. It is not approval of an account or financial terms, and does not mean a charge was completed.</p>
      <Link to="/forms" className="forms-button forms-button-secondary">Return to forms</Link>
    </section>
  </main>;

  return <main className="forms-shell payment-preview">
    <div className="forms-topline"><span>Premier Pharmaceuticals</span><span>Practice onboarding</span></div>
    <header className="forms-form-head">
      <Link to="/forms" className="forms-back">← All forms</Link>
      <p className="forms-eyebrow">Official paperwork</p>
      <h1>Payment Information</h1>
      <p>Provide signed payment instructions for manual processing. Alternatively, payment can be processed at checkout for online orders.</p>
    </header>
    <div className="forms-instructions"><span className="forms-instruction-icon" aria-hidden="true">i</span><p>Choose your payment method, complete the applicable information, review the supplied financial terms, and sign. Submitting this form does not process a payment or approve financial terms.</p></div>
    {configError && <div className="forms-alert forms-alert-warning" role="status">{configError} <button type="button" onClick={retryConfig}>Check again</button></div>}
    {config && !config.available && <div className="forms-alert forms-alert-warning" role="status">{config.unavailableReason || "Online submissions are temporarily unavailable."} You may review this form, but submission is disabled.</div>}
    {!config && !configError && <div className="forms-config-loading" role="status"><span /> Checking secure submission availability</div>}
    {config?.adminTesting && <div className="forms-alert forms-alert-warning" role="status"><strong>Administrator testing only.</strong> Public submissions remain disabled.</div>}
    {message && <div className={`forms-alert ${status === "failed" || status === "processing" || status === "received" || immutableConflict ? "forms-alert-warning" : "forms-alert-error"}`} role="alert">
      {message}{reference && status !== "ready" && <span className="forms-alert-reference"> Reference: {reference}</span>}
      {immutableConflict && <button type="button" onClick={startNewAttempt}>Start a new submission</button>}
    </div>}
    <section className="payment-preview-method" aria-label="Payment method">
      <div><h2>Choose Your Payment Method</h2></div>
      <div className="payment-preview-tabs" role="group" aria-label="Payment method">
        <button type="button" aria-pressed={method === "credit-card"} className={method === "credit-card" ? "is-active" : ""} onClick={() => selectMethod("credit-card")} disabled={submitting}>Credit Card</button>
        <button type="button" aria-pressed={method === "ach"} className={method === "ach" ? "is-active" : ""} onClick={() => selectMethod("ach")} disabled={submitting}>ACH</button>
      </div>
    </section>
    <form className="payment-preview-sheet" onSubmit={handleSubmit} noValidate>
      <div className="payment-preview-sheet-head">
        <div><p className="payment-preview-kicker">Premier Pharmaceuticals, LLC</p><h2>Payment authorization</h2></div>
        <span className="payment-preview-stamp">SIGNED INSTRUCTIONS · MANUAL PROCESSING</span>
      </div>
      <fieldset className="forms-section">
        <legend><span>01</span>Business identity</legend>
        <div className="payment-preview-grid">{field("legalCompanyName", "Legal company name", { wide: true, maxLength: 65 })}</div>
      </fieldset>
      {method === "credit-card" ? <fieldset className="forms-section">
        <legend><span>02</span>Credit card details</legend>
        <div className="payment-preview-grid">
          {field("cardholder", "Name on card", { maxLength: 65 })}
          {field("cardNumber", "Card number", { wide: true, maxLength: 25, inputMode: "numeric" })}
          {field("expiration", "Expiration · MM/YYYY", { maxLength: 7 })}
          {field("cvv", "CVV", { type: "password", maxLength: 4, inputMode: "numeric" })}
        </div>
      </fieldset> : <fieldset className="forms-section">
        <legend><span>02</span>Bank account details</legend>
        <div className="payment-preview-account-types">
          <span className="payment-preview-label">Account type</span>
          <div className="payment-preview-choices">
            {(["Checking", "Savings"] as const).map(option => <label key={option} className={`payment-preview-choice${values.accountType === option ? " is-selected" : ""}`}>
              <input type="radio" name="accountType" value={option} checked={values.accountType === option} autoComplete="off" data-private="true" onChange={() => update("accountType", option)} />{option}
            </label>)}
          </div>
          {errors["data.accountType"] && <p className="forms-error">{errors["data.accountType"]}</p>}
        </div>
        <div className="payment-preview-grid">
          {field("accountHolder", "Account holder", { maxLength: 65 })}
          {field("accountNumber", "Account number", { maxLength: 17, inputMode: "numeric" })}
          {field("routingNumber", "Routing number", { maxLength: 9, inputMode: "numeric" })}
          {field("bankName", "Bank name", { maxLength: 65 })}
          {field("bankAddress", "Bank address", { maxLength: 65 })}
          {field("bankCityStateZip", "City, state, ZIP", { maxLength: 65 })}
        </div>
      </fieldset>}
      <fieldset className="forms-section">
        <legend><span>03</span>Authorized signer</legend>
        <div className="payment-preview-grid">
          {field("printedName", "Printed name", { maxLength: 45 })}
          {field("title", "Title", { maxLength: 30 })}
          <div className="payment-preview-field is-wide">
            <label className="payment-preview-label" htmlFor="payment-signature">Typed signature</label>
            <input id="payment-signature" className={`forms-control payment-preview-input payment-preview-signature-input${errors.signerName ? " is-invalid" : ""}`}
              value={signerName} maxLength={140} autoComplete="off" data-private="true"
              aria-invalid={!!errors.signerName} onChange={event => { setSignerName(event.target.value); setErrors(previous => { const next = { ...previous }; delete next.signerName; return next; }); }} />
            <div className="payment-preview-signature" aria-label="Signature preview">{signerName}</div>
            {errors.signerName && <p className="forms-error">{errors.signerName}</p>}
            <p className="forms-hint">The typed signature must match the printed name.</p>
          </div>
        </div>
        <label className={`forms-consent${errors.consent ? " has-error" : ""}`}>
          <input type="checkbox" checked={consent} onChange={event => { setConsent(event.target.checked); setErrors(previous => { const next = { ...previous }; delete next.consent; return next; }); }} />
          <span>{SIGNATURE_CONSENT}</span>
        </label>
        {errors.consent && <p className="forms-error">{errors.consent}</p>}
      </fieldset>
      {method === "ach" && <section className="forms-section payment-preview-authorization">
        <h3>ACH authorization</h3><p>{PAYMENT_PREVIEW_ACH_AUTHORIZATION}</p><p>{PAYMENT_PREVIEW_ACH_TERMINATION}</p>
      </section>}
      <fieldset className="forms-section">
        <legend><span>04</span>Financial terms</legend>
        <p className="payment-preview-terms">{PAYMENT_PREVIEW_TERMS}</p>
        <div className="payment-preview-choices payment-preview-terms-choices" role="radiogroup" aria-label="Payment terms">
          {PAYMENT_TERMS.map(choice => <label key={choice} className={`payment-preview-choice${values.terms === choice ? " is-selected" : ""}`}>
            <input type="radio" name="terms" value={choice} checked={values.terms === choice} autoComplete="off" data-private="true" onChange={() => update("terms", choice)} />{choice}
          </label>)}
        </div>
        {errors["data.terms"] && <p className="forms-error">{errors["data.terms"]}</p>}
        <p className="payment-preview-fee">{PAYMENT_PREVIEW_FEE_NOTE}</p>
        {method === "credit-card" && <label className={`forms-consent payment-preview-fee-check${errors["data.feeAcknowledgment"] ? " has-error" : ""}`}>
          <input type="checkbox" checked={values.feeAcknowledgment === "Yes"} autoComplete="off" data-private="true" onChange={event => update("feeAcknowledgment", event.target.checked ? "Yes" : "")} />
          <span>I acknowledge the credit-card fee note.</span>
        </label>}
        {errors["data.feeAcknowledgment"] && <p className="forms-error">{errors["data.feeAcknowledgment"]}</p>}
      </fieldset>
      {errors.form && <div className="forms-alert forms-alert-error">{errors.form}</div>}
      <div className="payment-preview-actions">
        <p className="payment-preview-disclaimer">Submitting saves signed instructions for staff to process manually. No charge or bank debit is initiated by this form. Automatic email is queued by the server after secure save.</p>
        <button type="submit" className="forms-button" disabled={!isAvailable || submitting}>
          {submitting ? "Sending securely…" : status && status !== "ready" ? "Retry submission" : "Submit signed instructions"}
        </button>
      </div>
    </form>
    <footer className="forms-footer"><span>Premier Pharmaceuticals</span><span>Questions? Contact our onboarding team.</span></footer>
  </main>;
}
